import React, { useEffect, useContext, useRef, useState } from 'react';
import ErrorBoundary from './ErrorBoundary';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import EXregister from './components/EXregister';
import DatabaseManagement from './components/DatabaseManagement';
import UserManagement from './components/UserManagement';
import DataIntegrity from './components/DataIntegrity';
import Logger from './components/Logger';
import APIKeys from './components/APIKeys';
import TrialRequests from './components/TrialRequests';
import RegisterThemes from './components/RegisterThemes';
import { useBranding } from './BrandingContext';
import Layout from './components/Layouts';
import AdminOverview from './components/admin/AdminOverview';
import ImportData from './components/ImportData';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import DatabaseExpired from './components/DatabaseExpired';
import axios from 'axios';
import { UserContext } from './UserContext';
import ProtectedRoute from './components/ProtectedRoute';
import Unauthorized from './components/Unauthorized';
import WorkspaceLoader from './components/WorkspaceLoader';
import { isTokenExpired } from './utils/tokenUtils';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import './App.css';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { clearRegisterSession } from './utils/registerSession';
import RegisterSessionRecovery from './components/RegisterSessionRecovery';

function AppShell({ user }) {
  const { isBranded, brand, brandingLoading } = useBranding();
  const location = useLocation();
  const contentRef = useRef(null);
  const authPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
  const isAuthPage = authPaths.some((p) => location.pathname === p);
  const isDashboardPage = location.pathname === '/dashboard';
  const isAdminPage = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const contentClassName = `content ${isAdminPage ? 'content--admin' : isDashboardPage ? 'content--dashboard' : 'content--scroll'}`;

  useEffect(() => {
    const pageTitles = {
      '/login': 'Sign in',
      '/register': 'Create account',
      '/forgot-password': 'Forgot password',
      '/reset-password': 'Reset password',
      '/dashboard': 'Dashboard',
      '/exregister': 'Equipment Register',
      '/importdata': 'Import Data',
      '/unauthorized': 'Access denied',
      '/database-expired': 'Subscription expired',
      '/admin': 'Operations overview',
      '/admin/users': 'User Management',
      '/admin/databases': 'Database Management',
      '/admin/trials': 'Trial Requests',
      '/admin/themes': 'Register themes',
      '/admin/data-integrity': 'Data Integrity',
      '/admin/logger': 'Admin Logs',
      '/admin/api-keys': 'API Keys',
    };
    const title = pageTitles[location.pathname] || 'EXREG';
    const brandTitle = isBranded && !isAuthPage ? `${brand.name} · EXREG` : 'EXREG';
    document.title = title === 'EXREG' ? brandTitle : `${title} | ${brandTitle}`;
  }, [location.pathname, isBranded, brand.name, isAuthPage]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (!contentRef.current) return;
      contentRef.current.scrollTop = 0;
      contentRef.current.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  // Keep the router and sign-in transition mounted while the tenant identity loads.
  if (brandingLoading && !isAuthPage) return <WorkspaceLoader />;

  return (
    <div className={`app-container ${isAuthPage ? 'app-container--auth' : 'app-container--shell'}`}>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      {!isAuthPage && <Navbar />}
      <main id="main-content" ref={contentRef} className={contentClassName} tabIndex={-1}>
        <ToastContainer
          position="bottom-right"
          autoClose={3500}
          hideProgressBar
          newestOnTop
          closeOnClick
        />
        <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/database-expired" element={<DatabaseExpired />} />
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Navigate to="/login" />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/exregister" element={<ProtectedRoute><EXregister /></ProtectedRoute>} />
            <Route path="/importdata" element={<ProtectedRoute requiredRole="admin|inspector|editor"><ImportData /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><Layout /></ProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="databases" element={<DatabaseManagement />} />
              <Route path="trials" element={<TrialRequests />} />
              <Route path="themes" element={<RegisterThemes />} />
              <Route path="data-integrity" element={<DataIntegrity />} />
              <Route path="logger" element={<Logger />} />
              <Route path="api-keys" element={<APIKeys />} />
            </Route>
            <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}

function SessionApp() {
const { user, setUser, sessionToken } = useContext(UserContext);
const [loading, setLoading] = useState(true)                      ;
const [isDatabaseExpired, setIsDatabaseExpired] = useState(false) ;
const [requiresContextRecovery, setRequiresContextRecovery] = useState(false);
const token = sessionToken;
useEffect(() => {
let active = true;
const sessionIsCurrent = () => active && localStorage.getItem('token') === token;
const checkTokenAndFetchUser = async () => {
if (token) {
if (isTokenExpired(token)) {
clearRegisterSession();
setUser(null)                                                                           ;
setIsDatabaseExpired(false);
} else {
try {
const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/me`, {
headers: { Authorization: `Bearer ${token}` },
withCredentials: true,
})                                                                                      ;
if (!sessionIsCurrent()) return;
setUser(response.data);
if (response.data.register_recovery_required || !response.data.database_name) {
  setRequiresContextRecovery(true);
  setLoading(false);
  return;
}
const expiryResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/settings/expiry`, {
headers: { Authorization: `Bearer ${token}` },
withCredentials: true,
})                                                                                                    ;
if (!sessionIsCurrent()) return;
setIsDatabaseExpired(Boolean(expiryResponse.data.isExpired && response.data.role !== 'admin'));
} catch (error) {
console.error('Error fetching user or expiry date:', error) ;
if (!sessionIsCurrent()) return;
setUser(null)                                               ;
setIsDatabaseExpired(false);
if ([401, 409].includes(error.response?.status)) setRequiresContextRecovery(true);
}
}
}
if (active) setLoading(false)                               ;
}                                                           ;
checkTokenAndFetchUser() ;
return () => { active = false; };
}, [token, setUser])     ;
if (loading) {
  return <WorkspaceLoader /> ;
}
if (requiresContextRecovery) return <RegisterSessionRecovery allowInvitation={Boolean(user?.register_recovery_required && !user.database_name)} />;
if (isDatabaseExpired) {
  return (
    <Router>
      <Routes>
        <Route path="*" element={<DatabaseExpired fullViewport />} />
      </Routes>
    </Router>
  );
}
return (
  <Router>
    <AppShell user={user} />
  </Router>
);
}

function App() {
  const { sessionToken, sessionChanged, resumeSession } = useContext(UserContext);
  return <>
    <SessionApp key={sessionToken || 'signed-out'} />
    <Dialog open={Boolean(sessionChanged)} disableEscapeKeyDown aria-labelledby="register-session-changed">
      <DialogTitle id="register-session-changed">Register changed in another tab</DialogTitle>
      <DialogContent>
        This workspace is paused so its work cannot be saved into another register.
        Your unsaved drafts are still here. Open the current register when you are ready to leave them.
      </DialogContent>
      <DialogActions><Button onClick={resumeSession} variant="contained">Open current register</Button></DialogActions>
    </Dialog>
  </>;
}
export default App;
